// @flow
import * as React from 'react';
import classNames from 'classnames';

import Text from 'sail/Text/Text';
import Box from 'sail/Box';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
} from 'sail/DataTable';
import {Caption, BodyMono, Heading} from 'sail/Text';
import {useHighlightElement} from 'docs/hooks';
import './TailorPropsTable.scss';

type TailorPropsTableProps = {|
  component: string,
  propInfo: any,
|};

// This is very similar to MarkdocAttributeReference.js - since this component is expected to
// go through a lot of change, I thought duplicating code made more sense before
// trying to find the correct abstractions.

function Code({children}) {
  const typeRef = useHighlightElement();

  return (
    <pre className="TailorPropsTableTypeBlock language-typescript">
      <code ref={typeRef} className="language-typescript">
        {children}
      </code>
    </pre>
  );
}

export const TailorPropsTable = ({
  component,
  propInfo,
}: TailorPropsTableProps) => {
  if (!propInfo) {
    return 'This component has no props.';
  }

  const props = propInfo.props;

  if (!Object.keys(props).length) {
    return 'This component has no props.';
  }

  const rows = (Object.entries(props): any).map(([name, attr], index) => {
    let descriptionRow = null;
    if (attr.description) {
      descriptionRow = (
        <DataTableRow key={`${name}-description`}>
          <DataTableCell colSpan={3}>
            <Caption color="gray">{attr.description}</Caption>
          </DataTableCell>
        </DataTableRow>
      );
    }
    const attributeRow = (
      <DataTableRow
        key={name}
        className={classNames({
          'TailorPropsTable-TableRow-hasDescription': !!descriptionRow,
        })}
      >
        <DataTableCell width={200} emphasized wrap={false}>
          <Box>
            <BodyMono>{name} </BodyMono>
            {attr.required && <Text color="invalid">{'(required)'}</Text>}
          </Box>
        </DataTableCell>
        <DataTableCell>
          <Code>{attr.type ? attr.type.name : 'unknown'}</Code>
        </DataTableCell>
      </DataTableRow>
    );

    return [attributeRow, descriptionRow];
  });

  return (
    <>
      <Heading
        style={{
          fontWeight: 700,
          marginTop: '18px',
          marginBottom: '16px',
          display: 'block',
        }}
      >
        Props
      </Heading>
      <DataTable className="TailorPropsTable" size="small">
        <DataTableHead columns={[{label: 'Name'}, {label: 'Type'}]} />
        <DataTableBody>{rows}</DataTableBody>
      </DataTable>
    </>
  );
};
